import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

const MyKeys = () => {
  const { user } = useAuth();
  const { success, error } = useNotification();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshing, setRefreshing] = useState(false);

  // Use useCallback to prevent infinite re-renders
  const fetchKeys = useCallback(async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      console.log('🔍 Fetching user keys...', {
        page: currentPage,
        search: searchTerm,
        status: statusFilter,
        userId: user?._id
      });

      const response = await axios.get('/api/users/my-keys', {
        params: {
          page: currentPage,
          limit: 10,
          search: searchTerm,
          status: statusFilter
        }
      });

      console.log('✅ Keys fetched:', response.data);

      setKeys(response.data.keys);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error('❌ Failed to fetch keys:', err);
      error('Failed to load your license keys');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentPage, searchTerm, statusFilter, user?._id, error]);

  // Initial load
  useEffect(() => {
    if (user) {
      fetchKeys();
    }
  }, [user, fetchKeys]);

  // Listen for key purchases from other components
  useEffect(() => {
    const handleKeyPurchase = () => {
      console.log('🔄 Key purchase detected, refreshing keys...');
      fetchKeys(true);
    };

    // Listen for custom events
    window.addEventListener('keyPurchased', handleKeyPurchase);
    return () => window.removeEventListener('keyPurchased', handleKeyPurchase);
  }, [fetchKeys]);

  // Auto-refresh every 30 seconds when component is active
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchKeys(true);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchKeys]);

  const handleRefresh = () => {
    fetchKeys(true);
    success('Keys refreshed!', 2000);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    success('License key copied to clipboard!', 2000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'used':
        return 'text-green-400 bg-green-600/20';
      case 'available':
        return 'text-blue-400 bg-blue-600/20';
      case 'expired':
        return 'text-red-400 bg-red-600/20';
      case 'revoked':
        return 'text-gray-400 bg-gray-600/20';
      default:
        return 'text-gray-400 bg-gray-600/20';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateExpiryDate = (purchaseDate, duration) => {
    if (!purchaseDate || !duration) return null;
    const expiry = new Date(purchaseDate);
    expiry.setDate(expiry.getDate() + duration);
    return expiry;
  };

  const isKeyExpired = (purchaseDate, duration) => {
    const expiryDate = calculateExpiryDate(purchaseDate, duration);
    return expiryDate && new Date() > expiryDate;
  };

  const filteredKeys = keys.filter(key => {
    const matchesSearch = key.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         key.product?.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (loading && !refreshing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card-astral p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">My License Keys</h1>
            <p className="text-gray-400">
              Manage and view all your purchased license keys. Total keys: {keys.length}
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-neon-blue"
          >
            {refreshing ? (
              <div className="flex items-center">
                <div className="spinner mr-2"></div>
                Refreshing...
              </div>
            ) : (
              '🔄 Refresh Keys'
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card-astral p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Search Keys
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by key or product name..."
              className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Filter by Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
            >
              <option value="">All Status</option>
              <option value="used">Active</option>
              <option value="available">Available</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                setCurrentPage(1);
              }}
              className="btn-neon-blue w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Keys Grid */}
      {filteredKeys.length === 0 ? (
        <div className="card-astral p-8 text-center">
          <h3 className="text-xl font-bold text-gray-400 mb-2">No License Keys Found</h3>
          <p className="text-gray-500 mb-4">
            {keys.length === 0 
              ? "You haven't purchased any license keys yet." 
              : "No keys match your current filters."
            }
          </p>
          {keys.length === 0 && (
            <div className="flex justify-center gap-3">
              <a href="/products" className="btn-neon">
                Browse Products
              </a>
              <a href="/purchase" className="btn-neon-blue">
                Purchase Keys
              </a>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredKeys.map((keyData) => {
            const expiryDate = calculateExpiryDate(keyData.purchasedAt, keyData.duration);
            const isExpired = isKeyExpired(keyData.purchasedAt, keyData.duration);
            
            return (
              <div key={keyData._id} className="card-astral p-6 hover:glow-purple transition-all duration-300">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-1">
                      {keyData.product?.name || 'Unknown Product'}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-purple-600/20 text-purple-400 text-xs rounded font-mono">
                        {keyData.product?.keyTag || 'N/A'}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded ${getStatusColor(keyData.status)}`}>
                        {keyData.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* License Key Display */}
                <div className="bg-dark-200 p-3 rounded-lg mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <span className="text-gray-400 text-xs block">License Key:</span>
                      <code className="text-purple-400 font-mono text-sm block break-all">
                        {keyData.key}
                      </code>
                    </div>
                    <button
                      onClick={() => copyToClipboard(keyData.key)}
                      className="text-gray-400 hover:text-purple-400 transition-colors ml-2"
                      title="Copy to clipboard"
                    >
                      📋
                    </button>
                  </div>
                </div>

                {/* Key Details */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Purchased:</span>
                    <span className="text-white">{formatDate(keyData.purchasedAt)}</span>
                  </div>
                  
                  {keyData.duration && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Duration:</span>
                      <span className="text-white">{keyData.duration} days</span>
                    </div>
                  )}
                  
                  {expiryDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Expires:</span>
                      <span className={`${isExpired ? 'text-red-400' : 'text-white'}`}>
                        {formatDate(expiryDate)}
                      </span>
                    </div>
                  )}
                  
                  {keyData.purchasePrice && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Cost:</span>
                      <span className="text-green-400">{keyData.purchasePrice} credits</span>
                    </div>
                  )}

                  {keyData.product?.productType && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Type:</span>
                      <span className="text-white capitalize">{keyData.product.productType}</span>
                    </div>
                  )}
                </div>

                {/* Activation Status */}
                {keyData.activations && keyData.activations.length > 0 && (
                  <div className="mt-4 p-3 bg-blue-600/10 rounded-lg">
                    <div className="text-blue-400 text-sm font-semibold mb-1">
                      Device Activations: {keyData.activations.length}
                    </div>
                    <div className="text-xs text-gray-400">
                      Last used: {formatDate(keyData.activations[0]?.lastUsed)}
                    </div>
                  </div>
                )}

                {/* Expiry Warning */}
                {expiryDate && !isExpired && (
                  <div className="mt-4">
                    {new Date() > new Date(expiryDate.getTime() - 7 * 24 * 60 * 60 * 1000) && (
                      <div className="p-2 bg-yellow-600/20 border border-yellow-500/50 rounded text-yellow-400 text-xs">
                        ⚠️ Expires in {Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24))} days
                      </div>
                    )}
                  </div>
                )}

                {/* Expired Notice */}
                {isExpired && (
                  <div className="mt-4 p-2 bg-red-600/20 border border-red-500/50 rounded text-red-400 text-xs">
                    ❌ This license has expired
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="btn-neon-blue disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="flex items-center px-4 py-2 text-white">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="btn-neon-blue disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default MyKeys;
